import React, { useState, useEffect } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getStudents } from "../../redux/actions/studentActions";
import { StudentSummaryDto } from "../../shared/dtos/student/StudentSummaryDto";
import useDebounce from "../../hooks/useDebounce";

interface StudentSearchSelectProps {
  value: string;
  onChange: (studentId: string) => void;
  branchId?: string;
  minWidth?: number | string;
}

const StudentSearchSelect: React.FC<StudentSearchSelectProps> = ({
  value,
  onChange,
  branchId,
  minWidth = 240,
}) => {
  const dispatch = useAppDispatch();
  const { items: students, loadingFetchAll: loading } = useAppSelector(
    (state) => state.student,
  );

  const [inputVal, setInputVal] = useState<string>("");
  const debouncedInput = useDebounce<string>(inputVal, 350);
  const [selectedStudent, setSelectedStudent] =
    useState<StudentSummaryDto | null>(null);

  useEffect(() => {
    if (debouncedInput.trim().length >= 2) {
      dispatch(
        getStudents({
          page: 1,
          size: 10,
          search: debouncedInput.trim(),
          branchId: branchId || undefined,
        }),
      );
    }
  }, [dispatch, debouncedInput, branchId]);

  // Keep internal selected state synchronized with value
  useEffect(() => {
    if (!value) setSelectedStudent(null);
  }, [value]);

  return (
    <Autocomplete<StudentSummaryDto, false, false, false>
      options={students}
      value={selectedStudent}
      loading={loading}
      getOptionLabel={(opt) =>
        `${opt.firstName} ${opt.lastName} (${opt.studentId})`
      }
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      onInputChange={(_, val, reason) => {
        if (reason === "input") setInputVal(val);
      }}
      onChange={(_, opt) => {
        setSelectedStudent(opt);
        onChange(opt ? opt.id : "");
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label="Filter by Student"
          placeholder="Type name or ID..."
          sx={{ minWidth }}
        />
      )}
    />
  );
};

export default StudentSearchSelect;
